import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import { countPending, flushOutbox, setOnline } from '@/Redux/Slices/syncSlice';

/**
 * How often the queue is re-counted while the tab is in front.
 *
 * Nothing tells this hook that a mutation was queued: call sites hand their work
 * to `@/lib/outbox` and know nothing about Redux. Without a poll the banner
 * would only be correct until the first queued action, which is exactly when it
 * starts to matter. The read is local storage, not a request.
 */
const POLL_MS = 10_000;

/**
 * §28 — flush the offline outbox when the connection returns or the tab comes
 * back to the front, and keep the pending count honest in between.
 *
 * `navigator.onLine` is a weak signal: false is reliable (the OS knows there is
 * no interface), true only means an interface exists — a captive portal or a
 * dead API answers `true`. So it is used to *start* a flush, never to claim
 * success; the flush result is what marks the app online again.
 */
export function useSync() {
  const dispatch = useAppDispatch();
  const sync = useAppSelector((state) => state.sync);

  useEffect(() => {
    dispatch(setOnline(navigator.onLine));
    void dispatch(countPending());
    if (navigator.onLine) void dispatch(flushOutbox());

    const goOnline = () => {
      dispatch(setOnline(true));
      void dispatch(flushOutbox());
    };
    const goOffline = () => dispatch(setOnline(false));

    // A backgrounded tab is throttled, so it can come back to the front holding
    // a count from minutes ago and a queue the browser never got to send.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void dispatch(countPending());
      if (navigator.onLine) void dispatch(flushOutbox());
    };

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void dispatch(countPending());
    }, POLL_MS);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [dispatch]);

  return sync;
}
